import { Link } from "react-router-dom";
import { X, Sparkles } from "lucide-react";

/**
 * Elegant upgrade modal — surfaced whenever an AI / feature call returns 402.
 * Handles every plan-limit code emitted by the backend:
 *   - advisor_capacity_reached   (global ai_insights monthly pool)
 *   - report_limit_reached       (max_reports_per_month)
 *   - strategy_limit_reached     (max_strategy_plans_per_month)
 *   - draft_limit_reached        (max_drafts_per_month)
 *   - checkin_limit_reached      (max_checkins_per_month)
 *   - profile_limit_reached      (max_profiles)
 *
 * Tone: non-shaming, dignified, premium. Never blames the user.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - detail: full 402 detail body from the backend
 */
const HEADER_BY_CODE = {
  advisor_capacity_reached: {
    eyebrow: "Capacity reached",
    headline: "You're using SugarSense like a personal advisor.",
    body: (d) => `You've used your monthly advisor capacity on ${d?.plan_name || "your current plan"}.`,
  },
  report_limit_reached: {
    eyebrow: "Report capacity reached",
    headline: "You've leaned on pattern reports a lot this month.",
    body: (d) => `You've used your ${d?.limit ?? "monthly"} Pattern Reports on ${d?.plan_name || "your current plan"}.`,
  },
  strategy_limit_reached: {
    eyebrow: "Strategy capacity reached",
    headline: "You're treating SugarSense like a strategist.",
    body: (d) => `You've used your ${d?.limit ?? "monthly"} Strategy Plans on ${d?.plan_name || "your current plan"}.`,
  },
  draft_limit_reached: {
    eyebrow: "Draft capacity reached",
    headline: "Lots of drafted notes this month — graceful.",
    body: (d) => `You've used your ${d?.limit ?? "monthly"} Message Drafts on ${d?.plan_name || "your current plan"}.`,
  },
  checkin_limit_reached: {
    eyebrow: "Check-in capacity reached",
    headline: "You're checking in consistently.",
    body: (d) => `You've used your ${d?.limit ?? "monthly"} Check-ins on ${d?.plan_name || "your current plan"}.`,
  },
  profile_limit_reached: {
    eyebrow: "Profile capacity reached",
    headline: "You're tracking multiple dynamics.",
    body: (d) => `${d?.plan_name || "Your current plan"} includes ${d?.max_profiles ?? "limited"} Relationship Profiles.`,
  },
};

const DEFAULT_HEADER = {
  eyebrow: "Capacity reached",
  headline: "You've reached this month's capacity.",
  body: (d) => `Your ${d?.plan_name || "current plan"} has refreshed monthly limits.`,
};

export default function UpgradeModal({ open, onClose, detail }) {
  if (!open) return null;
  const code = detail?.code || "advisor_capacity_reached";
  const header = HEADER_BY_CODE[code] || DEFAULT_HEADER;
  const suggested = detail?.upgrade_suggestion?.plan_key || "plus";
  const reason = detail?.upgrade_suggestion?.reason || "More capacity and richer relationship tracking.";
  return (
    <div
      data-testid="upgrade-modal-backdrop"
      className="fixed inset-0 z-50 bg-cocoa/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        data-testid="upgrade-modal"
        data-testid-code={code}
        onClick={(e) => e.stopPropagation()}
        className="ss-card-premium w-full max-w-md p-6 sm:p-7 relative"
      >
        <button
          onClick={onClose}
          data-testid="upgrade-modal-close"
          aria-label="Close"
          className="absolute top-3 right-3 text-cocoa/50 hover:text-cocoa"
        >
          <X size={18} />
        </button>
        <p className="text-[10px] tracking-[0.4em] uppercase text-mauve">{header.eyebrow}</p>
        <h2 className="font-heading text-2xl sm:text-3xl text-cocoa mt-2 leading-tight">
          {header.headline}
        </h2>
        <p className="mt-3 text-cocoa/75 text-[14px] leading-relaxed">
          {header.body(detail)} {reason}
        </p>
        <p className="text-[12px] text-cocoa/55 mt-3 italic">
          Your saved profiles, timeline, and intelligence are still fully accessible. Your capacity refreshes next month.
        </p>
        <div className="mt-5 flex flex-col sm:flex-row gap-2">
          <Link to="/billing" data-testid="upgrade-modal-cta" className="ss-pill ss-pill-gold h-11 flex-1 justify-center">
            <Sparkles size={14} /> See {suggested.charAt(0).toUpperCase() + suggested.slice(1)} plan
          </Link>
          <button onClick={onClose} data-testid="upgrade-modal-later" className="ss-pill h-11 flex-1 justify-center">Maybe later</button>
        </div>
        <p className="text-[11px] text-cocoa/45 mt-4 text-center">
          <Link to="/fair-usage" className="underline hover:text-cocoa">How capacity works</Link>
        </p>
      </div>
    </div>
  );
}

/**
 * Helper used by App.jsx error handlers to decide whether a 402 response
 * should surface the upgrade modal vs. just inline-error.
 */
export const LIMIT_CODES = new Set([
  "advisor_capacity_reached",
  "report_limit_reached",
  "strategy_limit_reached",
  "draft_limit_reached",
  "checkin_limit_reached",
  "profile_limit_reached",
]);

export function isLimitReached(err) {
  if (err?.response?.status !== 402) return false;
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object" && detail.code) {
    return LIMIT_CODES.has(detail.code);
  }
  return false;
}
